// Components
import { IconCreditCard } from '@u21/tabler-icons';
import {
  DataProps,
  SidebarExternalLinkList,
} from 'app/modules/sidebar/components/SidebarExternalLinkList';
import {
  U21Subsection,
  U21TitleCountLabel,
  U21Typography,
  U21Loading,
} from 'app/shared/u21-ui/components';

// Models
import { ShortInstrumentResponse } from 'app/modules/instruments/responses';

// Helpers
import { useMemo } from 'react';
import { formatDatetime } from 'app/shared/utils/date';
import { ROUTES_MAP } from 'app/shared/utils/routes';
import { useSelector } from 'react-redux';
import { selectHasReadInstrumentsPermission } from 'app/modules/session/selectors';
import { ColdStorageLoadingStatus } from 'app/shared/utils/fastArchHelpers';
import { ColdStorageCGDOMessage } from 'app/shared/components/DataLoader/ColdStorageCGDOMessage';

interface Props {
  count: number | null;
  instruments: ShortInstrumentResponse[];
  loading: boolean;
  coldStorageLoadingStatus?: ColdStorageLoadingStatus;
}

export const SidebarAssociatedInstruments = ({
  count,
  instruments,
  loading,
  coldStorageLoadingStatus,
}: Props) => {
  const countLoading =
    loading ||
    coldStorageLoadingStatus === ColdStorageLoadingStatus.LOADING_TIMEOUT;
  const hasReadInstrumentsPermission = useSelector(
    selectHasReadInstrumentsPermission,
  );
  const list = useMemo(
    () =>
      instruments.map<DataProps>((i) => ({
        label: formatDatetime(i.created_at),
        value: i.external_id,
        id: i.id,
        route: hasReadInstrumentsPermission
          ? ROUTES_MAP.instrumentsId.path.replace(':id', String(i.id))
          : undefined,
      })),
    [instruments, hasReadInstrumentsPermission],
  );
  return (
    <U21Subsection
      aria-label="Flagged Instruments"
      collapsible
      collapsed
      shadedHeader
      title={
        <U21TitleCountLabel loading={countLoading} count={count} label="">
          Flagged Instruments
        </U21TitleCountLabel>
      }
      titleIcon={<IconCreditCard />}
    >
      {coldStorageLoadingStatus && (
        <ColdStorageCGDOMessage
          coldStorageStatus={coldStorageLoadingStatus}
          cgdoType="instrument"
        />
      )}
      {loading ? (
        <U21Loading variant="dots" loading={loading} />
      ) : (
        <>
          {instruments.length ? (
            <SidebarExternalLinkList list={list} />
          ) : (
            <U21Typography variant="caption" color="text.secondary">
              No flagged instruments
            </U21Typography>
          )}
        </>
      )}
    </U21Subsection>
  );
};
